import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  PhoneInput,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Text,
  Typography,
} from '@fse/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { farmersControllerList } from '../api/generated/farmers/farmers';
import {
  useCooperativesControllerInviteMember,
  useCooperativesControllerRemoveMember,
} from '../api/generated/cooperatives/cooperatives';
import type { FarmerDto } from '../api/generated/models';

const PAGE_SIZE = 20;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatGps(gps: FarmerDto['farmGps']): string | null {
  if (!gps) return null;
  return `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`;
}

export function FarmersScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const farmersQuery = useQuery({
    queryKey: ['farmers', { limit: PAGE_SIZE, offset: 0 }],
    queryFn: () => farmersControllerList({ limit: PAGE_SIZE, offset: 0 }),
  });

  function invalidateFarmers() {
    // Catch every ['farmers', ...] query regardless of pagination params.
    queryClient.invalidateQueries({ queryKey: ['farmers'] });
  }

  function handleInvited(phoneE164: string) {
    invalidateFarmers();
    setToast(t('farmers.toast.invited', { phone: phoneE164 }));
  }

  function handleRemoved() {
    invalidateFarmers();
    setToast(t('farmers.toast.removed'));
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Text variant="headline-medium" as="h2">
          {t('farmers.title')}
        </Text>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={() => setInviteOpen(true)}
            startIcon={<Icon name="call" size={20} />}
          >
            {t('farmers.inviteByPhone')}
          </Button>
          <Link to="/farmers/new" style={{ textDecoration: 'none' }}>
            <Button variant="contained" startIcon={<Icon name="person_add" size={20} />}>
              {t('farmers.addFarmer')}
            </Button>
          </Link>
        </Stack>
      </Box>

      {farmersQuery.isPending ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
            </Stack>
          </CardContent>
        </Card>
      ) : farmersQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => farmersQuery.refetch()}>
              {t('farmers.retry')}
            </Button>
          }
        >
          {t('farmers.loadError', { error: String(farmersQuery.error) })}
        </Alert>
      ) : farmersQuery.data.data.length === 0 ? (
        <Card>
          <CardHeader title={t('farmers.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('farmers.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <FarmersTable
          rows={farmersQuery.data.data}
          total={farmersQuery.data.pagination.total}
          onRemoved={handleRemoved}
        />
      )}

      {inviteOpen ? (
        <InviteDialog onClose={() => setInviteOpen(false)} onInvited={handleInvited} />
      ) : null}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

interface FarmersTableProps {
  rows: FarmerDto[];
  total: number;
  onRemoved: () => void;
}

function FarmersTable({ rows, total, onRemoved }: FarmersTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Stack spacing={1.5}>
      <Text variant="body-medium" color="text.secondary">
        {t('farmers.count', { count: total })}
        {rows.length < total ? ' ' + t('farmers.showingFirst', { count: rows.length }) : ''}
      </Text>
      <TableContainer component={Card}>
        <Table size="small" aria-label={t('farmers.table.aria')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('farmers.table.name')}</TableCell>
              <TableCell>{t('farmers.table.phone')}</TableCell>
              <TableCell>{t('farmers.table.language')}</TableCell>
              <TableCell align="right">{t('farmers.table.trees')}</TableCell>
              <TableCell align="right">{t('farmers.table.capacity')}</TableCell>
              <TableCell>{t('farmers.table.gps')}</TableCell>
              <TableCell>{t('farmers.table.registered')}</TableCell>
              <TableCell align="right">{t('farmers.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((farmer) => (
              <FarmerRow
                key={farmer.userId}
                farmer={farmer}
                onNavigate={() =>
                  navigate({ to: '/farmers/$userId', params: { userId: farmer.userId } })
                }
                onRemoved={onRemoved}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

interface FarmerRowProps {
  farmer: FarmerDto;
  onNavigate: () => void;
  onRemoved: () => void;
}

function FarmerRow({ farmer, onNavigate, onRemoved }: FarmerRowProps) {
  const { t } = useTranslation();
  // Destructive remove goes through a confirm Dialog (TD-085). stopPropagation
  // on the action cell so a button click never triggers the row's navigate.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const removeMutation = useCooperativesControllerRemoveMember({
    mutation: {
      onSuccess: () => {
        setConfirmOpen(false);
        onRemoved();
      },
    },
  });

  const gps = formatGps(farmer.farmGps);

  return (
    <TableRow hover onClick={onNavigate} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {/* Real anchor for keyboard / right-click "open in new tab";
            the row onClick handles the common click-anywhere case. */}
        <Link
          to="/farmers/$userId"
          params={{ userId: farmer.userId }}
          style={{ textDecoration: 'none', fontWeight: 600 }}
          onClick={(e) => e.stopPropagation()}
        >
          {farmer.displayName}
        </Link>
      </TableCell>
      <TableCell>{farmer.phone}</TableCell>
      <TableCell>
        <Chip kind="assist" color="neutral" label={farmer.preferredLang.toUpperCase()} />
      </TableCell>
      <TableCell align="right">{farmer.treeCount}</TableCell>
      <TableCell align="right">
        {farmer.estCapacityKg ?? (
          <Typography component="span" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {gps ? (
          <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
            {gps}
          </Typography>
        ) : (
          <Typography component="span" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>{dateFormatter.format(new Date(farmer.registeredAt))}</TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
        <Button size="small" variant="text" color="error" onClick={() => setConfirmOpen(true)}>
          {t('farmers.remove.action')}
        </Button>

        <Dialog
          open={confirmOpen}
          onClose={() => {
            if (!removeMutation.isPending) setConfirmOpen(false);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            <Text variant="title-large" as="span">
              {t('farmers.remove.title')}
            </Text>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('farmers.remove.body', { name: farmer.displayName })}
            </DialogContentText>
            {removeMutation.isError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {String(removeMutation.error)}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              disabled={removeMutation.isPending}
              onClick={() => setConfirmOpen(false)}
            >
              {t('farmers.remove.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={removeMutation.isPending}
              onClick={() => removeMutation.mutate({ userId: farmer.userId })}
            >
              {removeMutation.isPending ? t('farmers.remove.removing') : t('farmers.remove.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}

interface InviteDialogProps {
  onClose: () => void;
  onInvited: (phoneE164: string) => void;
}

/**
 * Invite-by-phone modal. Adds the person to the cooperative as a claimable
 * (pin-less) member, then surfaces the result via a Snackbar on the parent.
 * Phone validation mirrors the add-farmer wizard: libphonenumber-js with
 * country context 'ZA', normalised to E.164 before POST. (TD-085: was an
 * inline toggled Card before @fse/ui gained a Dialog primitive.)
 */
function InviteDialog({ onClose, onInvited }: InviteDialogProps) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const inviteMutation = useCooperativesControllerInviteMember({
    mutation: {
      onSuccess: (result) => {
        onInvited(result.phoneE164);
        onClose();
      },
    },
  });

  function close() {
    if (inviteMutation.isPending) return;
    onClose();
  }

  function submit() {
    setPhoneError(null);

    const parsed = parsePhoneNumberFromString(phone, 'ZA');
    if (!phone || !parsed?.isValid()) {
      setPhoneError(t('farmers.invite.phoneInvalid'));
      return;
    }

    // TD-110 — no name field: the claimable account is minted under the phone;
    // the farmer sets their real name on /auth/claim. The generated DTO still
    // types displayName required (TD-109), so send the phone as a placeholder.
    inviteMutation.mutate({ data: { phoneE164: parsed.number, displayName: parsed.number } });
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <Text variant="title-large" as="span">
          {t('farmers.invite.title')}
        </Text>
        <IconButton aria-label={t('farmers.invite.close')} size="small" onClick={close} disabled={inviteMutation.isPending}>
          <Icon name="close" size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('farmers.invite.body')}
        </DialogContentText>
        <Stack spacing={2}>
          <PhoneInput
            value={phone}
            onChangeText={setPhone}
            error={phoneError ?? undefined}
            hint={t('farmers.invite.phoneHint')}
            disabled={inviteMutation.isPending}
          />

          {inviteMutation.isError ? (
            <Alert severity="error">
              {t('farmers.invite.sendError', { error: String(inviteMutation.error) })}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={inviteMutation.isPending} onClick={close}>
          {t('farmers.invite.cancel')}
        </Button>
        <Button variant="contained" disabled={inviteMutation.isPending} onClick={submit}>
          {inviteMutation.isPending ? t('farmers.invite.sending') : t('farmers.invite.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
